import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHelper } from '@app/_helpers';
import { AuthenticationService, VendorRequestService } from '@app/_services';
import { AiTokensService } from '../account/pages/page-ai-tokens/ai-tokens.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ToastrService } from 'ngx-toastr';

@UntilDestroy()
@Component({
    selector: 'app-ai-tokens-success',
    templateUrl: './ai-tokens-success.component.html',
    styleUrls: ['./ai-tokens-success.component.scss'],
    standalone: true,
    imports: [CommonModule, TranslateModule],
    providers: [TranslateHelper, AiTokensService]
})
export class AiTokensSuccessComponent implements OnInit {
    purchaseData: any = null;
    packageData: any = null;
    isLoading: boolean = true;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        public translateHelper: TranslateHelper,
        private translateService: TranslateService,
        public authService: AuthenticationService,
        private aiTokensService: AiTokensService,
        private vendorRequestService: VendorRequestService,
        private toastr: ToastrService
    ) {}

    ngOnInit(): void {
        console.log('[AI Tokens Success] Component initialized');
        // Load recent purchase data
        this.loadPurchaseData();
    }

    private loadPurchaseData(): void {
        console.log('[AI Tokens Success] Loading purchase data...');
        // Get the most recent purchase for current user
        this.aiTokensService.getLatestPurchase().pipe(untilDestroyed(this)).subscribe({
            next: (response: any) => {
                console.log('[AI Tokens Success] Purchase data response:', response);
                this.isLoading = false;
                if (response.success && response.data) {
                    this.purchaseData = response.data;
                    this.packageData = response.data;
                    console.log('[AI Tokens Success] Purchase data loaded successfully');
                } else {
                    console.log('[AI Tokens Success] No purchase data in response');
                }
            },
            error: (error) => {
                console.error('[AI Tokens Success] Error loading purchase data:', error);
                this.isLoading = false;
            }
        });
    }

    goToAITokensPage(): void {
        const user = this.authService.currentUserValue;
        
        // If user is a vendor, go to admin
        if (user && user.isVendor) {
            this.vendorRequestService.getAdminUrl().subscribe({
                next: (response: any) => {
                    if (response.code === 200 && response.url) {
                        window.location.href = response.url;
                    } else {
                        this.toastr.error(response.msg || 'Unable to navigate to admin page', 'Error');
                    }
                },
                error: (error) => {
                    console.error('Error getting admin URL:', error);
                    this.toastr.error('Unable to navigate to admin page. Please ensure you are logged in.', 'Error');
                }
            });
        } else {
            // If user is a customer, go to account AI tokens page
            this.router.navigate(['/account/ai-tokens']);
        }
    }

    getPackageName(): string {
        if (!this.packageData?.packageName) return '-';
        const lang = this.translateService.currentLang || 'en';
        const name = this.packageData.packageName;
        
        // If it's an object with translations
        if (typeof name === 'object' && name !== null) {
            return name[lang] || name.en || name.ar || '-';
        }
        // If it's a string
        return name;
    }

    getCurrencyCode(): string {
        if (!this.packageData?.currency) return 'BHD';
        const currency = this.packageData.currency;
        
        // If it's already a string (code), return it
        if (typeof currency === 'string') return currency;
        // If it's an object with en/ar properties
        return currency.en || currency.ar || 'BHD';
    }

    formatNumber(num: number): string {
        return num?.toLocaleString() || '0';
    }
}
